<template>
  <div>
    <b-sidebar
      id="sidebar-backdrop"
      backdrop
      shadow
      bg-variant="white"
      sidebar-class="sidebar-lg"
      no-header
      right
      :visible="sidebarActive"
      @change="(val) => $emit('update:sidebar-active', val)"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ isEditing ? 'Editar' : 'Nueva' }} Categoría
          </h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
        <!-- Campos formulario -->
        <validation-observer ref="simpleRules">
          <b-form
            v-if="show"
            class="p-2"
            @submit="onSubmit"
            @reset="onReset"
          >
            <!-- Nombre -->
            <validation-provider
              v-slot="{ errors }"
              name="nombre"
              rules="required"
            >
              <b-form-group
                label="Nombre:"
                label-for="input-name"
              >
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  autocomplete="new-password"
                  placeholder="Ingresa el nombre de la categoría"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Descripción -->
            <validation-provider
              v-slot="{ errors }"
              name="descripción"
            >
              <b-form-group
                label="Descripción:"
                label-for="input-description"
              >
                <b-form-textarea
                  id="input-description"
                  v-model="form.description"
                  placeholder="Ingresa una descripción (opcional)"
                  rows="3"
                  max-rows="6"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Estado -->
            <validation-provider
              v-slot="{ errors }"
              name="estado"
              rules="required"
            >
              <b-form-group
                label="Estado:"
                label-for="select-status"
              >
                <v-select
                  v-model="form.status"
                  :options="statusOptions"
                  :reduce="status => status.value"
                  :clearable="false"
                  placeholder="Selecciona el estado"
                  input-id="select-status"
                  required
                >
                  <template slot="no-options">
                    Lo siento, no se encontraron opciones
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-button
              type="submit"
              variant="primary"
            >
              {{ isEditing ? 'Actualizar' : 'Crear' }}
            </b-button>
            <b-button
              style="margin-left: 5px;"
              type="reset"
              variant="secondary"
            >
              {{ isEditing ? 'Restaurar' : 'Limpiar' }}
            </b-button>
          </b-form>
        </validation-observer>
      </template>

    </b-sidebar>
  </div>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import { required } from '@/utils/validations/validations'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  model: {
    prop: 'sidebarActive',
    event: 'update:sidebar-active',
  },
  props: {
    sidebarActive: {
      type: Boolean,
      default: false,
    },
    categoryId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      required,
      show: true,
      form: {
        name: '',
        description: '',
        status: 'Y',
      },
      originalForm: {
        name: '',
        description: '',
        status: 'Y',
      },
      statusOptions: [
        { label: 'Activo', value: 'Y' },
        { label: 'Inactivo', value: 'N' },
      ],
    }
  },
  computed: {
    isEditing() {
      return this.categoryId !== null && this.categoryId !== undefined && this.categoryId > 0
    },
  },
  watch: {
    sidebarActive(newVal) {
      if (newVal && this.categoryId) {
        this.getCategory()
      } else if (newVal && !this.categoryId) {
        this.resetForm()
      }
    },
  },
  methods: {
    async getCategory() {
      try {
        const response = await axios.get(`category/${this.categoryId}`)
        const category = response.data.data

        this.form = {
          name: category.name,
          description: category.description || '',
          status: category.status,
        }

        this.originalForm = { ...this.form }
      } catch (error) {
        console.error('Error fetching category:', error)
        this.makeToast('danger', 'Error', 'Error al cargar la categoría')
        this.$emit('update:sidebar-active', false)
      }
    },

    async onSubmit(event) {
      event.preventDefault()

      try {
        const isValid = await this.$refs.simpleRules.validate()

        if (!isValid) {
          return
        }

        let response
        if (this.isEditing) {
          response = await axios.put(`category/${this.categoryId}`, this.form)
        } else {
          response = await axios.post('category', this.form)
        }

        this.makeToast('success', 'Éxito', response.data.message || (this.isEditing ? 'Categoría actualizada exitosamente' : 'Categoría creada exitosamente'))

        this.resetForm()
        this.$emit('refreshData')
        this.$emit('update:sidebar-active', false)
      } catch (error) {
        console.error('Error saving category:', error)
        let errorMessage = this.isEditing ? 'Error al actualizar la categoría' : 'Error al crear la categoría'

        if (error.response && error.response.data && error.response.data.errors) {
          const { errors } = error.response.data
          errorMessage = Object.values(errors).flat().join(', ')
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        }

        this.makeToast('danger', 'Error', errorMessage)
      }
    },

    onReset(event) {
      event.preventDefault()
      if (this.isEditing) {
        this.form = { ...this.originalForm }
      } else {
        this.resetForm()
      }
      this.$refs.simpleRules.reset()
    },

    resetForm() {
      this.form = {
        name: '',
        description: '',
        status: 'Y',
      }
      this.originalForm = { ...this.form }
    },
  },
}
</script>

<style lang="scss" scoped>
.content-sidebar-header {
  border-bottom: 1px solid #d8d6de;
}
</style>
